import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  getDoc
} from 'firebase/firestore';
import { db } from './firebase';

export const companyService = {
  // Listar todas as empresas
  async getAll() {
    try {
      const companiesRef = collection(db, 'companies');
      const q = query(companiesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching all companies:', error);
      throw new Error('Erro ao buscar empresas');
    }
  },

  // Buscar empresa por ID
  async getById(companyId) {
    try {
      if (!companyId) {
        throw new Error('ID da empresa é obrigatório');
      }
      
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        const data = { id: companySnap.id, ...companySnap.data() };
        return data;
      } else {
        throw new Error('Empresa não encontrada');
      }
    } catch (error) {
      console.error('Error fetching company by ID:', {
        error: error,
        message: error.message,
        code: error.code,
        companyId: companyId
      });
      
      // Re-throw com mais contexto
      if (error.code === 'permission-denied') {
        throw new Error('Acesso negado às informações da empresa');
      } else if (error.message.includes('não encontrada')) {
        throw error; // Re-throw as is
      } else {
        throw new Error(`Erro ao buscar empresa: ${error.message}`);
      }
    }
  },

  // Buscar empresas ativas
  async getActive() {
    try {
      const companiesRef = collection(db, 'companies');
      const q = query(
        companiesRef, 
        where('active', '==', true), 
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching active companies:', error);
      throw new Error('Erro ao buscar empresas ativas');
    }
  },

  // Buscar empresas por proprietário
  async getByOwner(ownerId) {
    try {
      const companiesRef = collection(db, 'companies');
      const q = query(
        companiesRef, 
        where('ownerId', '==', ownerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching companies by owner:', error);
      throw new Error('Erro ao buscar empresas do proprietário');
    }
  },

  // Criar nova empresa
  async create(companyData, userId = null) {
    try {
      const companiesRef = collection(db, 'companies');
      const docRef = await addDoc(companiesRef, {
        ...companyData,
        ownerId: userId,
        active: companyData.active !== undefined ? companyData.active : true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      return {
        id: docRef.id,
        ...companyData,
        ownerId: userId,
        active: companyData.active !== undefined ? companyData.active : true
      };
    } catch (error) {
      console.error('Error creating company:', error);
      throw new Error('Erro ao criar empresa');
    }
  },

  // Atualizar empresa
  async update(companyId, companyData) {
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        ...companyData,
        updatedAt: new Date().toISOString()
      });
      
      return {
        id: companyId,
        ...companyData
      };
    } catch (error) {
      console.error('Error updating company:', error);
      throw new Error('Erro ao atualizar empresa');
    }
  },

  // Deletar empresa
  async delete(companyId) {
    try {
      const companyRef = doc(db, 'companies', companyId);
      await deleteDoc(companyRef);
      return true;
    } catch (error) {
      console.error('Error deleting company:', error);
      throw new Error('Erro ao deletar empresa');
    }
  },

  // Ativar/Desativar empresa
  async toggleStatus(companyId, active) {
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        active,
        updatedAt: new Date().toISOString()
      });
      
      return { id: companyId, active };
    } catch (error) {
      console.error('Error toggling company status:', error);
      throw new Error('Erro ao alterar status da empresa');
    }
  },

  // Buscar estatísticas de empresas
  async getStats() {
    try {
      const companies = await this.getAll();
      const totalCompanies = companies.length;
      const activeCompanies = companies.filter(c => c.active).length;
      const inactiveCompanies = totalCompanies - activeCompanies;
      
      return {
        total: totalCompanies,
        active: activeCompanies,
        inactive: inactiveCompanies,
        companies
      };
    } catch (error) {
      console.error('Error fetching company stats:', error);
      throw new Error('Erro ao buscar estatísticas das empresas');
    }
  },

  // Buscar configurações de expiração de pontos da empresa
  async getExpirationSettings(companyId) {
    try {
      const company = await this.getById(companyId);
      return company.pointsExpiration || {
        enabled: false,
        months: 12
      };
    } catch (error) {
      console.error('Error fetching expiration settings:', error);
      return {
        enabled: false,
        months: 12
      };
    }
  },

  // Atualizar permissão de acesso ao módulo de aluguéis
  async updateRentalAccess(companyId, hasRentalAccess) {
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        rentalAccess: hasRentalAccess,
        updatedAt: new Date()
      });
      return true;
    } catch (error) {
      console.error('Error updating rental access:', error);
      throw new Error('Erro ao atualizar acesso ao módulo de aluguéis');
    }
  },

  // Verificar se a empresa tem acesso ao módulo de aluguéis
  async hasRentalAccess(companyId) {
    try {
      const company = await this.getById(companyId);
      return company.rentalAccess === true;
    } catch (error) {
      console.error('Error checking rental access:', error);
      return false;
    }
  }
};